/**
 * Design System — single source of truth for all tokens.
 * Used by ThemeContext (JS) and mirrored in design-system.css (CSS variables).
 * Never import this in component files — use CSS variables instead.
 * Only ThemeContext and chart configs should consume the JS values directly.
 */

// ─── Color Palettes ────────────────────────────────────────────────────────────

export const palette = {
  // Blues (primary)
  blue50:  '#eff6ff',
  blue100: '#dbeafe',
  blue200: '#bfdbfe',
  blue400: '#60a5fa',
  blue500: '#3b82f6',
  blue600: '#2563eb',
  blue700: '#1d4ed8',
  blue900: '#1e3a8a',

  // Greens (success)
  green50:  '#f0fdf4',
  green100: '#dcfce7',
  green400: '#4ade80',
  green500: '#22c55e',
  green600: '#16a34a',
  green700: '#15803d',

  // Ambers (warning)
  amber50:  '#fffbeb',
  amber100: '#fef3c7',
  amber400: '#fbbf24',
  amber500: '#f59e0b',
  amber600: '#d97706',

  // Reds (danger)
  red50:  '#fef2f2',
  red100: '#fee2e2',
  red400: '#f87171',
  red500: '#ef4444',
  red600: '#dc2626',
  red700: '#b91c1c',

  // Cyans (info)
  cyan50:  '#ecfeff',
  cyan100: '#cffafe',
  cyan400: '#22d3ee',
  cyan500: '#06b6d4',
  cyan600: '#0891b2',

  // Slates (neutrals — surface, text, border)
  slate50:  '#f8fafc',
  slate100: '#f1f5f9',
  slate200: '#e2e8f0',
  slate300: '#cbd5e1',
  slate400: '#94a3b8',
  slate500: '#64748b',
  slate600: '#475569',
  slate700: '#334155',
  slate800: '#1e293b',
  slate850: '#172032',
  slate900: '#0f172a',
  slate950: '#020617',

  white: '#ffffff',
  black: '#000000',
};

// ─── Semantic Color Tokens ─────────────────────────────────────────────────────

export const tokens = {
  light: {
    // Core semantic — the same indigo-violet used by the login/signup/reset pages
    colorPrimary:       '#4F3EE0',
    colorPrimaryHover:  '#3D2FB8',
    colorPrimaryMuted:  '#ECE9FC',
    colorPrimaryText:   '#4F3EE0',

    colorSuccess:       '#16A369',
    colorSuccessHover:  '#0F7A4F',
    colorSuccessMuted:  '#E1F8ED',
    colorSuccessText:   '#0F7A4F',

    colorWarning:       '#E0A020',
    colorWarningHover:  '#B37A16',
    colorWarningMuted:  '#FCF1DD',
    colorWarningText:   '#B37A16',

    colorDanger:        '#E14848',
    colorDangerHover:   '#BE3434',
    colorDangerMuted:   '#FCE8E8',
    colorDangerText:    '#BE3434',

    colorInfo:          '#2E93DD',
    colorInfoHover:     '#2274B0',
    colorInfoMuted:     '#E7F3FC',
    colorInfoText:      '#2274B0',

    // Surface
    colorBg:            '#E9E6F6',
    colorSurface:       palette.white,
    colorSurfaceRaised: palette.white,
    colorSurfaceHover:  '#F3F1FB',

    // Text
    colorText:          '#1E1C2E',
    colorTextMuted:     '#6E6B8C',
    colorTextSubtle:    '#9694B3',
    colorHeading:       '#1E1C2E',
    colorTextInverse:   palette.white,

    // Border
    colorBorder:        '#D9D5EE',
    colorBorderStrong:  '#CBC8E0',

    // Table
    colorTableHeader:   '#EFEEF9',
    colorTableRowHover: '#F3F1FB',
    colorTableBorder:   '#E3E1F0',

    // Input
    colorInputBg:       palette.white,
    colorInputBorder:   '#E3E1F0',
    colorInputFocus:    '#4F3EE0',

    // Overlay
    colorOverlay:       'rgba(19, 18, 32, 0.55)',
  },

  dark: {
    // Core semantic — same indigo-violet family as light mode, lightened for a dark ground
    colorPrimary:       '#8B7FF0',
    colorPrimaryHover:  '#A79BFA',
    colorPrimaryMuted:  'rgba(139, 127, 240, 0.18)',
    colorPrimaryText:   '#B7AEF6',

    colorSuccess:       '#4ADE9C',
    colorSuccessHover:  '#6EE8B0',
    colorSuccessMuted:  'rgba(74, 222, 156, 0.16)',
    colorSuccessText:   '#7BEBB4',

    colorWarning:       '#F5C56B',
    colorWarningHover:  '#F8D488',
    colorWarningMuted:  'rgba(245, 197, 107, 0.16)',
    colorWarningText:   '#F8D488',

    colorDanger:        '#F08A8A',
    colorDangerHover:   '#F5A5A5',
    colorDangerMuted:   'rgba(240, 138, 138, 0.16)',
    colorDangerText:    '#F5A5A5',

    colorInfo:          '#7EC0F0',
    colorInfoHover:     '#9DD1F5',
    colorInfoMuted:     'rgba(126, 192, 240, 0.16)',
    colorInfoText:      '#9DD1F5',

    // Surface
    colorBg:            '#131220',
    colorSurface:       '#191728',
    colorSurfaceRaised: '#221F38',
    colorSurfaceHover:  'rgba(255, 255, 255, 0.06)',

    // Text
    colorText:          '#EDEBF7',
    colorTextMuted:     '#9694B3',
    colorTextSubtle:    '#6E6B8C',
    colorHeading:       '#F5F3FC',
    colorTextInverse:   '#131220',

    // Border
    colorBorder:        '#322F4A',
    colorBorderStrong:  '#443F63',

    // Table
    colorTableHeader:   '#191728',
    colorTableRowHover: 'rgba(255, 255, 255, 0.05)',
    colorTableBorder:   '#322F4A',

    // Input
    colorInputBg:       '#191728',
    colorInputBorder:   '#443F63',
    colorInputFocus:    '#8B7FF0',

    // Overlay
    colorOverlay:       'rgba(0, 0, 0, 0.72)',
  },

  // Sidebar — theme-aware (light lavender-tinted panel in light mode, deep
  // indigo-black in dark mode); a per-account custom color can still
  // override --sidebar-bg at runtime on top of whichever of these is active.
  sidebar: {
    bg:          '#ECE7FB',
    bgLighter:   '#FFFFFF',
    bgDarker:    '#DED5F7',
    border:      '#DCD3F5',
    text:        '#4D4A66',
    textMuted:   '#6E6B8C',
    active:      '#1E1C2E',
    activeBg:    '#FFFFFF',
    activeText:  '#1E1C2E',
    hover:       '#DFD6F7',
    width:       '280px',
  },

  // Header — matches the sidebar's tinted panel, also theme-aware
  header: {
    bg:          '#ECE7FB',
    bgGradient:  'linear-gradient(90deg, #ECE7FB 0%, #ECE7FB 100%)',
    border:      '#DCD3F5',
    text:        '#1E1C2E',
    icon:        '#4F3EE0',
    height:      '64px',
    mobileHeight:'56px',
  },

  // Status badge colors (shared across light/dark — only bg changes)
  status: {
    available:   { bg: '#dcfce7', text: '#15803d', darkBg: 'rgba(16, 185, 129, 0.15)', darkText: '#34d399' },
    reserved:    { bg: '#fef3c7', text: '#d97706', darkBg: 'rgba(245, 158, 11, 0.15)',  darkText: '#fbbf24' },
    active:      { bg: '#dbeafe', text: '#1d4ed8', darkBg: 'rgba(59, 130, 246, 0.15)', darkText: '#60a5fa' },
    maintenance: { bg: '#fee2e2', text: '#b91c1c', darkBg: 'rgba(239, 68, 68, 0.15)',  darkText: '#f87171' },
    completed:   { bg: '#f3f4f6', text: '#374151', darkBg: 'rgba(100,116,139, 0.15)',  darkText: '#94a3b8' },
    cancelled:   { bg: '#f3f4f6', text: '#6b7280', darkBg: 'rgba(100,116,139, 0.15)',  darkText: '#64748b' },
    pending:     { bg: '#fef3c7', text: '#d97706', darkBg: 'rgba(245, 158, 11, 0.15)', darkText: '#fbbf24' },
  },

  // Chart colors (always fixed for readability)
  charts: {
    primary:   palette.blue500,
    success:   '#10b981',
    warning:   palette.amber500,
    danger:    '#ef4444',
    info:      '#06b6d4',
    purple:    '#8b5cf6',
    orange:    '#f97316',
    pink:      '#ec4899',
    series: [palette.blue500, '#10b981', palette.amber500, '#ef4444', '#8b5cf6', '#f97316', '#06b6d4', '#ec4899'],
  },
};

// ─── Typography ────────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: "'Outfit', 'Segoe UI', system-ui, -apple-system, sans-serif",
  fontSize: {
    xs:   '0.75rem',   // 12px
    sm:   '0.875rem',  // 14px
    base: '1rem',      // 16px
    lg:   '1.125rem',  // 18px
    xl:   '1.25rem',   // 20px
    '2xl':'1.5rem',    // 24px
    '3xl':'1.875rem',  // 30px
    '4xl':'2.25rem',   // 36px
  },
  fontWeight: {
    regular: '400',
    medium:  '500',
    semibold:'600',
    bold:    '700',
  },
  lineHeight: {
    tight:  '1.25',
    snug:   '1.375',
    normal: '1.5',
    relaxed:'1.625',
  },
};

// ─── Spacing (4px base unit) ───────────────────────────────────────────────────

export const spacing = {
  0:   '0',
  1:   '0.25rem',  // 4px
  2:   '0.5rem',   // 8px
  3:   '0.75rem',  // 12px
  4:   '1rem',     // 16px
  5:   '1.25rem',  // 20px
  6:   '1.5rem',   // 24px
  8:   '2rem',     // 32px
  10:  '2.5rem',   // 40px
  12:  '3rem',     // 48px
  16:  '4rem',     // 64px
  20:  '5rem',     // 80px
};

// ─── Border Radius ─────────────────────────────────────────────────────────────

export const radius = {
  none: '0',
  sm:   '0.25rem',   // 4px
  md:   '0.375rem',  // 6px
  lg:   '0.5rem',    // 8px
  xl:   '0.75rem',   // 12px
  '2xl':'1rem',      // 16px
  full: '9999px',
};

// ─── Shadows ───────────────────────────────────────────────────────────────────

export const shadows = {
  light: {
    sm: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)',
    md: '0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -1px rgba(0,0,0,0.05)',
    lg: '0 10px 15px -3px rgba(0,0,0,0.08), 0 4px 6px -2px rgba(0,0,0,0.04)',
    xl: '0 20px 25px -5px rgba(0,0,0,0.08), 0 10px 10px -5px rgba(0,0,0,0.03)',
  },
  dark: {
    sm: '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
    md: '0 4px 6px -1px rgba(0,0,0,0.3), 0 2px 4px -1px rgba(0,0,0,0.2)',
    lg: '0 10px 15px -3px rgba(0,0,0,0.35), 0 4px 6px -2px rgba(0,0,0,0.2)',
    xl: '0 20px 25px -5px rgba(0,0,0,0.4), 0 10px 10px -5px rgba(0,0,0,0.2)',
  },
};

// ─── Z-index ───────────────────────────────────────────────────────────────────

export const zIndex = {
  base:     0,
  raised:   10,
  dropdown: 1000,
  sidebar:  1040,
  header:   1030,
  footer:   1020,
  overlay:  1039,
  modal:    1060,
  toast:    1100,
  mobileToggle: 1200,
};

// ─── Breakpoints ───────────────────────────────────────────────────────────────

export const breakpoints = {
  mobile:  768,   // < 768px
  tablet:  992,   // 768–991px
  desktop: 1200,  // ≥ 992px
};

// ─── Transitions ───────────────────────────────────────────────────────────────

export const transitions = {
  fast:   '0.15s ease',
  normal: '0.2s ease',
  slow:   '0.3s ease',
  theme:  'background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease',
};
